/* eslint-disable max-lines */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'clsx';
import { ChevronRightIcon } from '@fronton/icons-react';
import Swiper from 'swiper';
import {
  List,
  MoreInfoCardGrid,
  ModalMediaCard,
  ModalMediaCardBody,
  ModalMediaCardImage,
  SecondarySection,
  SectionSubTitle,
  SwiperWithNavigation,
  SwiperSlide,
  Typography,
} from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils';
import { analyticsUtils, ClickButtonElementName } from 'utils/analytics-utils';
import { STORAGE_ELEMENT_BLOCK } from '../../storage-constants';
import { STORAGE_STYLES_CARD_LIST, StorageStylesSectionStructure } from './storage-styles-section-constants';
import {
  StorageStyleCard,
  storageStyleCardBottom,
  storageStyleCardName,
  storageStyleCardNameIcon,
  storageStyleCardNameIconWrapper,
  storageStyleImage,
  storageStylesDescription,
  storageStylesList,
  modalMediaCardSwiper,
  storageStyleCardSlide,
} from './storage-styles-section-styles';

export const StorageStylesSection = () => {
  const bannerRefs = useRef<HTMLDivElement[]>([]);
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isCardClicked, setIsCardClicked] = useState<boolean>(false);
  const [openCardIndex, setOpenCardIndex] = useState<number | undefined>(undefined);

  const promotions = useMemo(
    () =>
      STORAGE_STYLES_CARD_LIST.map((card, index: number) => ({
        name: `${STORAGE_ELEMENT_BLOCK}_bottom`,
        creative: t(card.name),
        position: (index + 1).toString(),
      })),
    [t]
  );

  usePromoViewEvent(bannerRefs, `${STORAGE_ELEMENT_BLOCK} / stili`, promotions);

  /**
   * Обработчик закрытия модального окна
   */
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    analyticsUtils.clickButtonEvent(
      STORAGE_ELEMENT_BLOCK,
      'Закрыть',
      `${t(STORAGE_STYLES_CARD_LIST[openCardIndex || 0].name)} / modal`
    );
  }, [openCardIndex, setIsModalOpen, t]);

  /**
   * Обработчик нажатия на карточку со стилями
   */
  const handleCardClick = useCallback(
    (index: number) => () => {
      setOpenCardIndex(index);

      analyticsUtils.promoClickEvent(`${STORAGE_ELEMENT_BLOCK} / stili`, promotions[index]);
      if (index === 0) {
        window.open('/uslugi/proektirovanie-i-dizayn/sistema-hraneniya-lion/', '_blank', 'noopener,noreferrer');
        return;
      }

      setIsModalOpen(true);
      setIsCardClicked(index > 0);
      analyticsUtils.showEvent(
        STORAGE_ELEMENT_BLOCK,
        'Модальное окно',
        `${t(STORAGE_STYLES_CARD_LIST[index].name)} / modal`
      );
    },
    [setIsModalOpen, setOpenCardIndex, promotions, t]
  );

  /**
   * Обработчик нажатия кнопки "Следующий слайд"
   */
  const handleNextSlideBtnClick = useCallback(
    ({ activeIndex }: Swiper) => {
      setOpenCardIndex(activeIndex);
      // Предотвращает срабатывание события GA при первом открытии карточки c индексом > 0
      if (isCardClicked) {
        setIsCardClicked(false);
        return;
      }
      analyticsUtils.clickButtonEvent(
        STORAGE_ELEMENT_BLOCK,
        ClickButtonElementName.SwitchForward,
        `${t(STORAGE_STYLES_CARD_LIST[activeIndex].name)} / modal`
      );
    },
    [isCardClicked, setOpenCardIndex, t]
  );

  /**
   * Обработчик нажатия кнопки "Предыдущий слайд"
   */
  const handlePrevSlideBtnClick = useCallback(
    ({ activeIndex }: Swiper) => {
      setOpenCardIndex(activeIndex);
      analyticsUtils.clickButtonEvent(
        STORAGE_ELEMENT_BLOCK,
        ClickButtonElementName.SwitchBackward,
        `${t(STORAGE_STYLES_CARD_LIST[activeIndex].name)} / modal`
      );
    },
    [setOpenCardIndex, t]
  );

  return (
    <SecondarySection>
      <SectionSubTitle adaptive={false} margin="s">
        {t('styles-section.title')}
      </SectionSubTitle>
      <Typography adaptive={false} className={storageStylesDescription} variant="l" size="body_short">
        {t('styles-section.description')}
      </Typography>
      <MoreInfoCardGrid cardPerRow={2}>
        {STORAGE_STYLES_CARD_LIST.map(
          ({ imgSrc, name, nameStyles }: StorageStylesSectionStructure, index: number) => (
            <StorageStyleCard
              ref={(el: HTMLDivElement | null) => {
                if (el && bannerRefs) bannerRefs.current[index] = el;
              }}
              data-position={index}
              onClick={handleCardClick(index)}
              key={createElementKey('storage-styles-card', index)}>
              <img alt={t(name) || ''} className={storageStyleImage} src={imgSrc} loading="lazy" />
              <div className={storageStyleCardBottom}>
                <Typography adaptive={false} className={cn(storageStyleCardName, nameStyles)} variant="h3">
                  {t(name)}
                </Typography>
                <div className={storageStyleCardNameIconWrapper}>
                  <ChevronRightIcon
                    className={storageStyleCardNameIcon}
                    type="outline"
                    bold
                    size="m"
                    color="text-primary"
                  />
                </div>
              </div>
            </StorageStyleCard>
          )
        )}
      </MoreInfoCardGrid>

      <ModalMediaCard show={isModalOpen} onClose={handleCloseModal}>
        <SwiperWithNavigation
          className={modalMediaCardSwiper}
          breakpoints={{
            320: { enabled: false },
            1024: { enabled: true },
          }}
          initialSlide={openCardIndex}
          allowSlidePrev={Boolean(openCardIndex && openCardIndex > 1)}
          onSlideNextTransitionEnd={handleNextSlideBtnClick}
          onSlidePrevTransitionEnd={handlePrevSlideBtnClick}
          slidesPerView="auto"
          watchOverflow>
          {STORAGE_STYLES_CARD_LIST.map(
            ({ modalTitle, descriptionList, modalImgSrc }: StorageStylesSectionStructure, index: number) => (
              <SwiperSlide
                className={storageStyleCardSlide}
                key={createElementKey('storage-styles-card', index)}>
                <ModalMediaCardBody>
                  <SectionSubTitle margin="s" adaptive={false} variant="h3">
                    {t(modalTitle)}
                  </SectionSubTitle>
                  <List className={storageStylesList}>
                    {descriptionList?.map(({ leftIcon, text }, descriptionIndex: number) => (
                      <li key={createElementKey('card-desc-list', descriptionIndex)}>
                        {leftIcon}
                        <Typography variant="m" size="body_long">
                          {t(text)}
                        </Typography>
                      </li>
                    ))}
                  </List>
                </ModalMediaCardBody>

                <ModalMediaCardImage alt={t(modalTitle) || ''} src={modalImgSrc} />
              </SwiperSlide>
            )
          )}
        </SwiperWithNavigation>
      </ModalMediaCard>
    </SecondarySection>
  );
};
